import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// Store
import store from './store';

// Theme
import { lightTheme, darkTheme } from './styles/theme';

// Components
import ProtectedRoute from './components/auth/ProtectedRoute';
import MainLayout from './components/layout/MainLayout';
import LoginForm from './components/auth/LoginForm';

// Pages
import Dashboard from './pages/Dashboard';
import Leads from './pages/Leads';
import LeadDetail from './pages/LeadDetail';
import Users from './pages/Users';
import UserCreate from './pages/UserCreate';
import Reports from './pages/Reports';
import Calls from './pages/Calls';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import NotFound from './pages/NotFound';
import Unauthorized from './pages/Unauthorized';
import WebhookDashboard from './pages/WebhookDashboard';
import Analytics from './pages/Analytics';
import Performance from './pages/Performance';
import Assignments from './pages/Assignments';
import Notifications from './pages/Notifications';
import LeadFlow from './pages/LeadFlow';
import IPDDone from './pages/IPDDone';

// Static Legal Pages
import DataDeletion from './pages/DataDeletion';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import About from './pages/About';

// Styles
import 'react-toastify/dist/ReactToastify.css';
import './styles/globals.css';

function App() {
  // You could make this dynamic based on user settings
  const [darkMode, setDarkMode] = React.useState(false);
  const theme = darkMode ? darkTheme : lightTheme;

  return (
    <Provider store={store}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Router>
            <Routes>
              
              {/* Public Routes */}
              <Route path="/login" element={<LoginForm />} />
              <Route path="/unauthorized" element={<Unauthorized />} />
              
              {/* Static Legal Pages - No Authentication Required */}
              <Route path="/about" element={<About />} />
              <Route path="/data-deletion" element={<DataDeletion />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />

              {/* Protected Routes with Layout */}
              <Route path="/" element={
                <ProtectedRoute>
                  <MainLayout />
                </ProtectedRoute>
              }>
                {/* Default redirect to dashboard */}
                <Route index element={<Navigate to="/dashboard" replace />} />
                
                {/* Dashboard */}
                <Route path="dashboard" element={<Dashboard />} />
                
                {/* Leads Routes */}
                <Route path="leads" element={<Leads />} />
                <Route path="leads/:id" element={<LeadDetail />} />
                
                {/* Users Routes */}
                <Route path="users" element={<Users />} />
                <Route path="users/create" element={
                  <ProtectedRoute requiredRoles={['admin', 'manager', 'team_lead']}>
                    <UserCreate />
                  </ProtectedRoute>
                } />
                
                {/* Reports */}
                <Route path="reports" element={<Reports />} />
                
                {/* Calls */}
                <Route path="calls" element={<Calls />} />
                
                {/* Settings & Profile */}
                <Route path="settings" element={<Settings />} />
                <Route path="profile" element={<Profile />} />
                
                {/* Admin Only Routes */}
                <Route path="webhooks" element={
                  <ProtectedRoute requiredRoles={['admin']}>
                    <WebhookDashboard />
                  </ProtectedRoute>
                } />
                
                <Route path="analytics" element={
                  <ProtectedRoute requiredRoles={['admin', 'manager']}>
                    <Analytics />
                  </ProtectedRoute>
                } />
                
                {/* Performance/Team Management for Managers and Team Leads */}
                <Route path="performance" element={
                  <ProtectedRoute requiredRoles={['admin', 'manager', 'team_lead']}>
                    <Performance />
                  </ProtectedRoute>
                } />
                
                <Route path="assignments" element={
                  <ProtectedRoute requiredRoles={['admin', 'manager', 'team_lead']}>
                    <Assignments />
                  </ProtectedRoute>
                } />
                
                {/* Notifications page */}
                <Route path="notifications" element={<Notifications />} />
                
                {/* Lead Flow Control for Team Leads, Managers and Admins */}
                <Route path="lead-flow" element={
                  <ProtectedRoute requiredRoles={['admin', 'manager', 'team_lead']}>
                    <LeadFlow />
                  </ProtectedRoute>
                } />

                {/* IPD Done Management for All Roles */}
                <Route path="ipd-done" element={
                  <ProtectedRoute requiredRoles={['admin', 'manager', 'team_lead', 'bda']}>
                    <IPDDone />
                  </ProtectedRoute>
                } />
              </Route>

              {/* 404 Route */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Router>
          
          {/* Toast Notifications */}
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme={darkMode ? 'dark' : 'light'}
            toastStyle={{
              fontSize: '14px',
              borderRadius: '8px',
            }}
          />
        </ThemeProvider>
      </LocalizationProvider>
    </Provider>
  );
}

export default App;